import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ExternalLink, AlertTriangle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { privacyConsentApi } from '../api/time-tracking.api'
import { Modal } from '@symphenia/ui-components'

interface PrivacyConsentModalProps {
  privacyText: string | null
  privacyUrl: string | null
  /** true, wenn überhaupt eine Datenschutzerklärung hinterlegt ist */
  policyConfigured?: boolean
  onConsent: () => void
  /** Dialog schließen, ohne zuzustimmen */
  onCancel: () => void
}

export default function PrivacyConsentModal({
  privacyText,
  privacyUrl,
  policyConfigured = true,
  onConsent,
  onCancel,
}: PrivacyConsentModalProps) {
  const { t } = useTranslation()
  const [checked, setChecked] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleConfirm = async () => {
    if (!checked) return
    setLoading(true)
    setError(null)
    try {
      await privacyConsentApi.grant()
      onConsent()
    } catch {
      setError(t('common.error', { defaultValue: 'Ein Fehler ist aufgetreten. Bitte versuche es erneut.' }))
    } finally {
      setLoading(false)
    }
  }

  const displayText =
    privacyText?.trim() ||
    t('timeTracking.privacyConsentDefault', {
      defaultValue:
        'Ich stimme der Verarbeitung meiner Arbeitszeitdaten gemäß Datenschutzerklärung zu.',
    })

  return (
    <Modal isOpen onClose={onCancel} dismissible={false} hideCloseButton size="md" className="max-w-md">
      {/* Header */}
      <h2 className="mb-2 text-xl font-semibold text-primary">
        {t('timeTracking.privacyConsent', { defaultValue: 'Datenschutz-Einwilligung' })}
      </h2>

      {!policyConfigured ? (
        // ── Keine Datenschutzerklärung hinterlegt ─────────────────────────────
        <>
          <div
            className="mb-5 flex items-start gap-3 rounded-md border px-3 py-3 text-sm"
            style={{ background: 'rgba(245,158,11,0.15)', borderColor: 'rgba(245,158,11,0.4)', color: '#b45309' }}
          >
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>
              {t('timeTracking.privacyConsentNotConfigured', {
                defaultValue:
                  'Es ist noch keine Datenschutzerklärung hinterlegt. Ohne sie kann die Einwilligung nicht erteilt werden. Bitte wende dich an deinen Administrator, damit eine Datenschutzerklärung hinterlegt wird.',
              })}
            </span>
          </div>
          <Button variant="outline" className="w-full" onClick={onCancel}>
            {t('common.close', { defaultValue: 'Schließen' })}
          </Button>
        </>
      ) : (
        // ── Normale Zustimmung ────────────────────────────────────────────────
        <>
          <p className="mb-4 text-sm text-muted-foreground">
            {t('timeTracking.privacyConsentRequired', {
              defaultValue:
                'Bitte stimme der Datenschutzerklärung zu, um die Zeiterfassung zu nutzen.',
            })}
          </p>

          {/* Privacy text */}
          <div className="mb-5 max-h-48 overflow-y-auto rounded-md border border-border bg-muted/40 p-3 text-sm leading-relaxed text-foreground">
            {displayText}
          </div>

          {/* Link zur vollständigen DSE */}
          {privacyUrl && (
            <div className="mb-4">
              <a
                href={privacyUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline"
              >
                <ExternalLink className="h-3.5 w-3.5 shrink-0" />
                {t('timeTracking.privacyConsentReadLink', { defaultValue: 'Datenschutzerklärung lesen' })}
              </a>
            </div>
          )}

          {/* Checkbox */}
          <label className="mb-5 flex cursor-pointer items-start gap-3">
            <input
              type="checkbox"
              checked={checked}
              onChange={(e) => setChecked(e.target.checked)}
              className="mt-0.5 h-4 w-4 shrink-0 accent-primary"
            />
            <span className="text-sm text-foreground">
              {t('timeTracking.privacyConsentCheckbox', {
                defaultValue:
                  'Ich habe die Datenschutzerklärung gelesen und stimme der Verarbeitung meiner Arbeitszeitdaten zu.',
              })}
            </span>
          </label>

          {/* Error */}
          {error && (
            <div className="mb-4 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {error}
            </div>
          )}

          {/* Buttons */}
          <div className="flex gap-3">
            <Button variant="outline" className="flex-1" disabled={loading} onClick={onCancel}>
              {t('common.cancel', { defaultValue: 'Abbrechen' })}
            </Button>
            <Button className="flex-1" disabled={!checked || loading} onClick={handleConfirm}>
              {loading
                ? t('common.loading', { defaultValue: 'Wird geladen...' })
                : t('timeTracking.privacyConsentConfirm', { defaultValue: 'Einwilligung bestätigen' })}
            </Button>
          </div>
        </>
      )}
    </Modal>
  )
}
